/**
 * Reconstruct an ordered list of timeline events from a single chatbot_log row.
 *
 * The schema only records the LAST attempt's timestamps (no per-attempt history),
 * so we surface a summary line for retries when attempts > 1. Document this
 * limitation in the UI via a tooltip.
 */
import {
  Sparkles,
  Calendar,
  Send,
  CheckCircle2,
  Inbox,
  Eye,
  AlertTriangle,
  Skull,
  RotateCw,
  Hourglass,
} from "lucide-react";

/**
 * @typedef {Object} TimelineEvent
 * @property {string} key      - stable id for React key
 * @property {string} ts       - ISO timestamp (or null for retry-summary line)
 * @property {string} label    - short human-readable label (vi)
 * @property {string} [detail] - secondary line (e.g. error text)
 * @property {"success"|"info"|"warning"|"error"|"neutral"} kind
 * @property {React.ComponentType} icon - lucide icon component
 */

/** @returns {TimelineEvent[]} */
export const buildTimeline = (log) => {
  if (!log) return [];

  const events = [];
  const attempts = log.attempts || 0;
  const maxAttempts = log.maxAttempts || 5;

  // 1. Trigger detected — always present
  if (log.createdAt) {
    events.push({
      key: "trigger",
      ts: log.createdAt,
      label: "Trigger phát hiện",
      detail: log.contactQuestion
        ? `"${truncate(log.contactQuestion, 80)}"`
        : undefined,
      kind: "info",
      icon: Sparkles,
    });
  }

  // 2. Scheduled (only if scheduled to a future time, not immediate)
  if (
    log.dateScheduled &&
    log.createdAt &&
    new Date(log.dateScheduled).getTime() - new Date(log.createdAt).getTime() >
      1000
  ) {
    events.push({
      key: "scheduled",
      ts: log.dateScheduled,
      label: "Đã lập lịch gửi",
      kind: "info",
      icon: Calendar,
    });
  }

  // 3. Sending started — last attempt timestamp
  if (log.sendingStartedAt) {
    events.push({
      key: "sending",
      ts: log.sendingStartedAt,
      label:
        attempts > 0
          ? `Bắt đầu gửi (lần ${attempts}/${maxAttempts})`
          : `Bắt đầu gửi`,
      kind: "info",
      icon: Send,
    });
  }

  // 3b. Retry summary (only if multiple attempts happened)
  if (attempts > 1) {
    events.push({
      key: "retry-summary",
      ts: null,
      label: `Đã retry ${attempts - 1} lần trước đó`,
      detail: "Hệ thống chỉ lưu mốc thời gian của lần gần nhất.",
      kind: "warning",
      icon: RotateCw,
    });
  }

  // 4. Sent successfully
  if (log.dateSent) {
    events.push({
      key: "sent",
      ts: log.dateSent,
      label: "Gửi thành công",
      kind: "success",
      icon: CheckCircle2,
    });
  }

  // 5. Received by Zalo server
  if (log.dateReceived) {
    events.push({
      key: "received",
      ts: log.dateReceived,
      label: "Khách đã nhận",
      kind: "success",
      icon: Inbox,
    });
  }

  // 6. Seen
  if (log.dateSeen) {
    events.push({
      key: "seen",
      ts: log.dateSeen,
      label: "Khách đã xem",
      kind: "success",
      icon: Eye,
    });
  }

  // 7. Failed (transient) or Dead (permanent)
  if (log.status === "failed" && log.dateFailed) {
    events.push({
      key: "failed",
      ts: log.dateFailed,
      label: `Lỗi (lần ${attempts || 1}/${maxAttempts})`,
      detail: log.lastError || "Không có thông tin lỗi",
      kind: "error",
      icon: AlertTriangle,
    });

    // Pending retry note
    if (log.nextRetryAt && new Date(log.nextRetryAt).getTime() > Date.now()) {
      events.push({
        key: "retry-pending",
        ts: log.nextRetryAt,
        label: "Sẽ thử lại",
        kind: "info",
        icon: Hourglass,
      });
    }
  } else if (log.status === "dead") {
    events.push({
      key: "dead",
      ts: log.dateFailed,
      label: `Đã dừng (max ${maxAttempts} lần)`,
      detail: log.lastError || "Bot sẽ không thử lại tin nhắn này.",
      kind: "error",
      icon: Skull,
    });
  }

  return events;
};

const truncate = (s, n) => {
  if (!s) return "";
  return s.length > n ? `${s.slice(0, n)}…` : s;
};
